import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Breadcrumb,
  Tag,
  Card,
  Row,
  Col,
  message,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HomeOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import { unitsService, Unit } from '@/renderer/services/unitsService'
import './UnitsList.css'

const UNIT_STATUSES = [
  { label: 'Available (AVLB)', value: 'AVLB', color: 'green' },
  { label: 'In Use (NUSE)', value: 'NUSE', color: 'blue' },
  { label: 'Inspection (INSP)', value: 'INSP', color: 'orange' },
  { label: 'Maintenance (MTCE)', value: 'MTCE', color: 'orange' },
  { label: 'Repair (REPR)', value: 'REPR', color: 'red' },
  { label: 'Quarantined (QRTD)', value: 'QRTD', color: 'red' },
  { label: 'Retired (RTRD)', value: 'RTRD', color: 'purple' },
]

const UNIT_TYPES = [
  { label: 'Truck', value: 'truck' },
  { label: 'Wireline Reel', value: 'wireline_reel' },
  { label: 'Slickline Reel', value: 'slickline_reel' },
  { label: 'Cable', value: 'cable' },
]

const COMPANY_LOCATIONS = [
  { label: 'Neuquén - Operaciones', value: 'Neuquén - Operaciones' },
  { label: 'Neuquén - Base', value: 'Neuquén - Base' },
  { label: 'Comodoro - Operaciones', value: 'Comodoro - Operaciones' },
  { label: 'Comodoro - Base', value: 'Comodoro - Base' },
  { label: 'Buenos Aires - Office', value: 'Buenos Aires - Office' },
]

interface ExtendedUnit extends Unit {
  key: string
}

export function UnitsList() {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [units, setUnits] = useState<ExtendedUnit[]>([])
  const [loading, setLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingUnit, setEditingUnit] = useState<ExtendedUnit | null>(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 })
  const [filters, setFilters] = useState({ status: '', location: '', unit_type: '' })

  useEffect(() => {
    loadUnits()
  }, [pagination.current, filters])

  const loadUnits = async () => {
    setLoading(true)
    try {
      const skip = (pagination.current - 1) * pagination.pageSize
      const response = await unitsService.listUnits(skip, pagination.pageSize, filters)
      const unitsWithKeys: ExtendedUnit[] = response.items.map((unit) => ({
        ...unit,
        key: unit.id,
      }))
      setUnits(unitsWithKeys)
      setPagination({ ...pagination, total: response.total })
    } catch (error) {
      message.error('Failed to load units')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    form.resetFields()
    setEditingUnit(null)
    setIsModalVisible(true)
  }

  const handleEdit = (unit: ExtendedUnit) => {
    form.setFieldsValue(unit)
    setEditingUnit(unit)
    setIsModalVisible(true)
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      if (editingUnit) {
        await unitsService.updateUnit(editingUnit.id, values)
        message.success('Unit updated successfully')
      } else {
        await unitsService.createUnit(values)
        message.success('Unit created successfully')
      }
      setIsModalVisible(false)
      setPagination({ ...pagination, current: 1 })
      await loadUnits()
    } catch (error) {
      console.error('Error saving unit:', error)
      message.error('Failed to save unit')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await unitsService.deleteUnit(id)
      message.success('Unit deleted successfully')
      await loadUnits()
    } catch (error) {
      console.error('Error deleting unit:', error)
      message.error('Failed to delete unit')
    }
  }

  const columns: ColumnsType<ExtendedUnit> = [
    {
      title: 'Unit Type',
      dataIndex: 'unit_type',
      key: 'unit_type',
      width: 120,
    },
    {
      title: 'Unit ID',
      dataIndex: 'unit_id',
      key: 'unit_id',
      width: 150,
    },
    {
      title: 'Serial Number',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig = UNIT_STATUSES.find((s) => s.value === status)
        return statusConfig ? <Tag color={statusConfig.color}>{statusConfig.label}</Tag> : status
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 150,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Popconfirm
            title="Delete Unit"
            description="Are you sure you want to delete this unit?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item>
          <a onClick={() => navigate('/inventory')}>
            <HomeOutlined />
          </a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Inventory</Breadcrumb.Item>
        <Breadcrumb.Item>Units</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 600 }}>Units Management</h1>
        <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
          Manage trucks, reels, cables, and other equipment units
        </p>
      </div>

      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space wrap>
              <Select
                placeholder="Filter by Status"
                style={{ width: 180 }}
                allowClear
                options={UNIT_STATUSES}
                onChange={(value) => {
                  setFilters({ ...filters, status: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
              <Select
                placeholder="Filter by Location"
                style={{ width: 180 }}
                allowClear
                options={COMPANY_LOCATIONS}
                onChange={(value) => {
                  setFilters({ ...filters, location: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
              <Select
                placeholder="Filter by Unit Type"
                style={{ width: 180 }}
                allowClear
                options={UNIT_TYPES}
                onChange={(value) => {
                  setFilters({ ...filters, unit_type: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
            </Space>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Unit
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={units}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (page) => setPagination({ ...pagination, current: page }),
        }}
        size="small"
      />

      <Modal
        title={editingUnit ? 'Edit Unit' : 'Add Unit'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Unit Type"
            name="unit_type"
            rules={[{ required: true, message: 'Unit Type is required' }]}
          >
            <Select placeholder="Select unit type" options={UNIT_TYPES} />
          </Form.Item>

          <Form.Item
            label="Unit ID"
            name="unit_id"
            rules={[{ required: true, message: 'Unit ID is required' }]}
          >
            <Input placeholder="e.g., Oklahoma, Reel-001" />
          </Form.Item>

          <Form.Item label="Serial Number" name="serial_number">
            <Input placeholder="Serial number" />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Status is required' }]}
          >
            <Select placeholder="Select status" options={UNIT_STATUSES} />
          </Form.Item>

          <Form.Item
            label="Location"
            name="location"
            rules={[{ required: true, message: 'Location is required' }]}
          >
            <Select placeholder="Select location" options={COMPANY_LOCATIONS} />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea placeholder="Additional description" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
